import { ButtonV2, DatePickerInput, Flex, FormControl, OnDatePickerChange, SpacingStack, TextV2 } from '@withjoy/joykit';
import { Guests, Search } from '@withjoy/joykit/icons';
import { AnalyticsContext, useTranslation } from '@shared/core';
import { OptionType, SelectV1 } from '@withjoy/joykit';
import { format } from 'date-fns-tz';
import React, { useState, useMemo, useContext } from 'react';
import { BookingContainer } from './Booking.styles';
import { useResponsive } from '@shared/utils/hooks/useResponsive';
import { useLocaleContext } from '@shared/core/i18n/LocaleContext';
import { GuestSiteFormTypography } from '@apps/guest/components/GuestSiteFormTypography';
import { addDays } from 'date-fns';
import { getZonedTime } from './Booking.utils';
import { GuestSiteTypographyOverride } from '@apps/guest/components/GuestSiteTypographyOverride/GuestSiteTypographyOverride';
import { label3ToCaptionOverride } from '@apps/guest/components/GuestSiteTypographyOverride/GuestSiteTypographyOverride.constants';
import { Link } from '@shared/core';
import { useEventInfo } from '@shared/utils/eventInfo';
import { useHotelRoutePaths } from '@apps/guest/routes/HotelBooking/HotelBooking.routes';
import { useEventCallback } from 'yet-another-react-lightbox';
import { formatDateForTravel } from '@shared/utils/dateHelpers';

interface Props
  extends Readonly<{
    address?: Maybe<string>;
    placeId?: Maybe<string>;
    timezone?: Maybe<string>;
    startTimeMs?: number;
    endTimeMs?: number;
  }> {}

export const Booking: React.FC<Props> = props => {
  const { address, placeId, timezone, startTimeMs, endTimeMs } = props;
  const { t } = useTranslation('guestSiteTravel');
  const { suggestedStayDatesTitle, searchButtonTitle, guestOptionPlural, guestOptionSingle, checkIn, checkOut } = t('booking');
  const [isMobile] = useResponsive({ values: { mobile: true, tablet: false } });
  const locale = useLocaleContext();

  const { eventHandle } = useEventInfo();
  const hotelRoute = useHotelRoutePaths();
  const analytics = useContext(AnalyticsContext);

  const mobileDateFormat = 'MMM dd';
  const mobileMask = '';
  const currentDateFormat = isMobile ? mobileDateFormat : locale.dateFormat;
  const currentDateMask = isMobile ? mobileMask : locale.dateInputMask;
  const today = new Date();

  const zonedStartDate = useMemo(() => getZonedTime(startTimeMs, timezone), [startTimeMs, timezone]);
  const zonedEndDate = useMemo(() => getZonedTime(endTimeMs, timezone), [endTimeMs, timezone]);

  const [guests, setGuests] = useState<OptionType>({
    label: `1 ${guestOptionSingle()}`,
    value: '1',
  });
  const [startDate, setStartDate] = useState<Date | null>(zonedStartDate || today);
  const [endDate, setEndDate] = useState<Date | null>(zonedEndDate || today);
  const onChange: OnDatePickerChange = dates => {
    if (Array.isArray(dates)) {
      const [start, end] = dates;
      setStartDate(start);
      setEndDate(end);
    }
  };

  const nextAfterStartDay = addDays(startDate || today, 1);
  const minStartDate = endDate ? today : nextAfterStartDay;

  const formattedStartDate = zonedStartDate && format(zonedStartDate, 'eee, MMMM dd');
  const formattedEndDate = zonedEndDate && format(zonedEndDate, 'eee, MMMM dd');

  const url = useMemo(() => {
    const hotelSearchPath = hotelRoute.hotelSearch.buildRelativeUrl(`${eventHandle}`, {
      utm_medium: 'web',
      utm_source: 'website_travel_page',
      utm_campaign: 'booking_assistant',
      ...(placeId && { placeId }),
      ...(address && { name: address }),
      ...(startDate && { checkInDate: formatDateForTravel(startDate) }),
      ...(endDate && { checkOutDate: formatDateForTravel(endDate) }),
    });
    return hotelSearchPath;
  }, [address, eventHandle, hotelRoute.hotelSearch, placeId, startDate, endDate]);

  const onClickSearch = useEventCallback(() => {
    analytics.track({
      action: 'BookingAssistantClicked',
      actionType: 'click',
      category: 'wedding',
      pagePrefix: 'guest',
      page: 'travel',
      label: 'hotelSearchLink',
    });
  });

  const guestOptions = [...Array(8)].map((el, i) => {
    const cur = i + 1;
    return {
      label: cur + ' ' + (cur === 1 ? guestOptionSingle() : guestOptionPlural()),
      value: cur.toString(),
    };
  });

  return (
    <BookingContainer
      paddingX={[6]}
      paddingY={[7]}
    >
      <SpacingStack spacing={6}>
        <div>
          <GuestSiteTypographyOverride override={label3ToCaptionOverride}>
            <TextV2
              typographyVariant="label3"
              color="mono12"
              paddingBottom={3}
            >
              {suggestedStayDatesTitle()}
            </TextV2>
          </GuestSiteTypographyOverride>
          <TextV2
            typographyVariant="body3"
            marginBottom={6}
          >
            {formattedStartDate} - {formattedEndDate}
          </TextV2>
          <TextV2 typographyVariant="body3">{address}</TextV2>
        </div>
        <GuestSiteFormTypography>
          <Flex>
            <GuestSiteTypographyOverride override={label3ToCaptionOverride}>
              <FormControl
                label={checkIn()}
                overrides={{
                  Label: {
                    props: {
                      typographyVariant: 'label3',
                      color: 'mono12',
                    },
                  },
                }}
              >
                <DatePickerInput
                  minDate={minStartDate}
                  isRange={true}
                  rangeDisplayValue="start"
                  onChange={onChange}
                  value={[startDate, endDate]}
                  monthsShown={1}
                  dateFormat={currentDateFormat}
                  mask={currentDateMask}
                  placeholder={currentDateFormat.toUpperCase()}
                />
              </FormControl>
              <FormControl
                label={checkOut()}
                marginLeft={6}
                overrides={{
                  Label: {
                    props: {
                      typographyVariant: 'label3',
                      color: 'mono12',
                    },
                  },
                }}
              >
                <DatePickerInput
                  minDate={nextAfterStartDay}
                  isRange={true}
                  rangeDisplayValue="end"
                  onChange={onChange}
                  value={[startDate, endDate]}
                  monthsShown={1}
                  dateFormat={currentDateFormat}
                  mask={currentDateMask}
                  placeholder={currentDateFormat.toUpperCase()}
                />
              </FormControl>
            </GuestSiteTypographyOverride>
          </Flex>
          <FormControl>
            <SelectV1
              searchable={false}
              iconLeft={<Guests />}
              defaultValue={guests}
              onChange={e => setGuests({ label: e?.label || '', value: e?.value || '1' })}
              options={guestOptions}
            />
          </FormControl>
        </GuestSiteFormTypography>
        <ButtonV2
          startIcon={<Search />}
          as={Link}
          to={url}
          onClick={onClickSearch}
          variant="solid"
          intent="primary"
        >
          {searchButtonTitle()}
        </ButtonV2>
      </SpacingStack>
    </BookingContainer>
  );
};
